import './draftTable.less'
import { Card, Input, Table } from 'antd'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import i18n from 'domain/i18n/i18n'
import React from 'react'
import RequiredLabel from 'components/RequiredLabel'
import robPermissions from 'constants/robPermissions'
import usePermission from 'hooks/usePermission'

const t = key => i18n.t(`rob.survey.soundingDraftAndTemperature.${key}`)

const getNestedError = (errors, path) =>
  path.split('.').reduce((acc, key) => acc?.[key], errors)

const calcTrim = (fwd, aft) => {
  const a = parseFloat(fwd)
  const f = parseFloat(aft)
  if (isNaN(a) || isNaN(f)) return ''

  return (a - f).toFixed(3)
}

const DraftTable = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext()
  const can = usePermission()
  const canEdit = can(robPermissions.EDIT_GENERAL)

  const fwdBefore = useWatch({
    control,
    name: 'general.soundingDraftAndTemperature.draft.fwd.before',
  })
  const fwdAfter = useWatch({
    control,
    name: 'general.soundingDraftAndTemperature.draft.fwd.after',
  })
  const aftBefore = useWatch({
    control,
    name: 'general.soundingDraftAndTemperature.draft.aft.before',
  })
  const aftAfter = useWatch({
    control,
    name: 'general.soundingDraftAndTemperature.draft.aft.after',
  })

  const trimBefore = calcTrim(fwdBefore, aftBefore)
  const trimAfter = calcTrim(fwdAfter, aftAfter)

  const renderInput = ({ fieldName, required }) => {
    const error = getNestedError(errors, fieldName)

    return (
      <Controller
        control={control}
        name={fieldName}
        render={({ field }) => (
          <div className="draft-table__input-wrap">
            <Input
              {...field}
              disabled={!canEdit}
              inputMode="decimal"
              status={error ? 'error' : ''}
              onChange={e =>
                field.onChange(e.target.value.replace(/[^\d.-]/g, ''))
              }
            />
            {required && <RequiredLabel required />}
            {error && (
              <div className="draft-table__error-msg">{error.message}</div>
            )}
          </div>
        )}
      />
    )
  }

  const ROWS = [
    {
      afterField: 'general.soundingDraftAndTemperature.draft.fwd.after',
      beforeField: 'general.soundingDraftAndTemperature.draft.fwd.before',
      key: 'fwd',
      label: t('draft.fwd'),
      required: true,
    },
    {
      afterField: 'general.soundingDraftAndTemperature.draft.mid.after',
      beforeField: 'general.soundingDraftAndTemperature.draft.mid.before',
      key: 'mid',
      label: t('draft.mid'),
      required: false,
    },
    {
      afterField: 'general.soundingDraftAndTemperature.draft.aft.after',
      beforeField: 'general.soundingDraftAndTemperature.draft.aft.before',
      key: 'aft',
      label: t('draft.aft'),
      required: true,
    },
    {
      isTrim: true,
      key: 'trim',
      label: t('draft.trim'),
    },
  ]

  const columns = [
    {
      dataIndex: 'label',
      key: 'label',
      title: '',
      width: 60,
    },
    {
      key: 'before',
      render: (_, record) =>
        record.isTrim ? (
          <Input
            className="draft-table__trim-input"
            disabled
            value={trimBefore}
          />
        ) : (
          renderInput({
            fieldName: record.beforeField,
            required: record.required,
          })
        ),
      title: t('draft.before'),
      width: 130,
    },
    {
      key: 'after',
      render: (_, record) =>
        record.isTrim ? (
          <Input
            className="draft-table__trim-input"
            disabled
            value={trimAfter}
          />
        ) : (
          renderInput({
            fieldName: record.afterField,
            required: record.required,
          })
        ),
      title: t('draft.after'),
      width: 130,
    },
  ]

  return (
    <Card className="draft-table__card" size="small" title={t('draft.title')}>
      <Table
        bordered
        columns={columns}
        dataSource={ROWS}
        pagination={false}
        rowKey="key"
      />
    </Card>
  )
}

export default DraftTable
